/**
 * Dev-sync J2 — annotation geometry: the pixel↔data math + hit-testing for
 * click-anywhere-to-add state points.
 *
 * Port of psychrometric_chart.js:2150-2235. Why this exists at all (legacy's
 * own note, js:2152-2162): Plotly's `plotly_click` only fires near an
 * EXISTING plotted point — clicking empty chart background never fires it,
 * and when it does fire the coordinates snap to the matched point. So the
 * chart uses a plain DOM click and its own pixel↔data math off `.nsewdrag`
 * (Plotly's transparent rect covering exactly the plotted area) + the live
 * `_fullLayout` axis ranges, read fresh on every call.
 *
 * ★ UNIT BOUNDARY (js:2181-2189): the live y-axis range is in gr/lb (the
 * display unit) but every stored `w` is lb/lb (what the backend computes and
 * expects back). pixelToData divides by GRAINS_PER_LB on the way OUT (so a
 * new annotation POSTs lb/lb); dataToPixel multiplies on the way IN.
 */

import type { PsyAnnotation } from './psychrometricApi';
import type { PlotlyChartNode } from './usePlotly';
import { GRAINS_PER_LB, toGrains } from './chartTraces';

export interface PlotAreaInfo {
  rect: DOMRect;
  xr: [number, number];
  /** gr/lb — the axis display range. */
  yr: [number, number];
}

/** The plotting area's current screen rect and axis ranges (js:2168-2179). */
export function plotArea(node: PlotlyChartNode | null): PlotAreaInfo | null {
  if (!node || !node._fullLayout) return null;
  const dragRect = node.querySelector('.nsewdrag');
  if (!dragRect) return null;
  const xaxis = node._fullLayout.xaxis;
  const yaxis = node._fullLayout.yaxis;
  if (!xaxis || !yaxis) return null;
  return {
    rect: dragRect.getBoundingClientRect(),
    xr: xaxis.range,
    yr: yaxis.range,
  };
}

/** Screen → data; null when outside the plotted area (js:2190-2200). */
export function pixelToData(
  node: PlotlyChartNode | null,
  clientX: number,
  clientY: number
): { t_db: number; w: number } | null {
  const pa = plotArea(node);
  if (!pa || !pa.rect.width || !pa.rect.height) return null;
  const fx = (clientX - pa.rect.left) / pa.rect.width;
  const fy = (clientY - pa.rect.top) / pa.rect.height;
  if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return null;
  return {
    t_db: pa.xr[0] + fx * (pa.xr[1] - pa.xr[0]),
    w: (pa.yr[1] - fy * (pa.yr[1] - pa.yr[0])) / GRAINS_PER_LB,
  };
}

/** Data (lb/lb) → screen (js:2202-2209). */
export function dataToPixel(
  node: PlotlyChartNode | null,
  t_db: number,
  w: number
): { clientX: number; clientY: number } | null {
  const pa = plotArea(node);
  if (!pa) return null;
  const wGrains = toGrains(w);
  const fx = (t_db - pa.xr[0]) / (pa.xr[1] - pa.xr[0]);
  const fy = 1 - (wGrains - pa.yr[0]) / (pa.yr[1] - pa.yr[0]);
  return {
    clientX: pa.rect.left + fx * pa.rect.width,
    clientY: pa.rect.top + fy * pa.rect.height,
  };
}

/**
 * "On this point" covers the dot AND its label — the label sits in a box
 * up-and-right of the dot (textposition "top right"), so a click there needs
 * its own box check, not just distance-to-dot. PAD extends both 5px past
 * their visual edges (js:2211-2235).
 */
export function findAnnotationNear(
  node: PlotlyChartNode | null,
  annotations: PsyAnnotation[],
  clientX: number,
  clientY: number,
  thresholdPx: number
): PsyAnnotation | null {
  const PAD = 5;
  let best: PsyAnnotation | null = null;
  let bestDist = thresholdPx + PAD;
  annotations.forEach((a) => {
    const px = dataToPixel(node, a.t_db, a.w);
    if (!px) return;
    let d = Math.hypot(px.clientX - clientX, px.clientY - clientY);
    if (a.label) {
      const dx = Math.abs(px.clientX - clientX);
      const dy = px.clientY - clientY; // positive = click is above the dot
      if (dx < 40 + PAD && dy > -4 - PAD && dy < 30 + PAD) d = 0;
    }
    if (d < bestDist) {
      bestDist = d;
      best = a;
    }
  });
  return best;
}
